import apiClient, { apiService } from './api';
import {
  RestaurantOrderDTO,
  RestaurantOrderListDTO,
  CreateRestaurantOrderDTO,
  UpdateOrderLinesDTO,
  IssueKotDTO,
  IssueKotResponseDTO,
  VoidOrderResponseDTO,
  VoidKotItemsDTO,
  VoidKotItemsResponseDTO,
  BillHistoryItemDTO,
} from '../types';

const API = '/restaurant-orders';

export const restaurantOrderService = {
  getAll: (pageNumber = 1, pageSize = 50, status?: string) =>
    apiService.get<{ data: RestaurantOrderListDTO[] }>(API, { pageNumber, pageSize, ...(status ? { status } : {}) }),

  getById: (id: number) =>
    apiService.get<RestaurantOrderDTO>(`${API}/${id}`),

  create: (data: CreateRestaurantOrderDTO) =>
    apiService.post<RestaurantOrderDTO>(API, data),

  hold: (data: Omit<CreateRestaurantOrderDTO, 'paymentMethod' | 'amountPaid' | 'changeGiven'>) =>
    apiService.post<RestaurantOrderDTO>(`${API}/hold`, data),

  updateStatus: (id: number, status: string) =>
    apiService.put<RestaurantOrderDTO>(`${API}/${id}/status`, { status }),

  updateLines: (id: number, data: UpdateOrderLinesDTO) =>
    apiService.put<RestaurantOrderDTO>(`${API}/${id}/lines`, data),

  issueKot: (id: number, data: IssueKotDTO) =>
    apiService.post<IssueKotResponseDTO>(`${API}/${id}/kot`, data),

  settle: (id: number, data: { paymentMethod?: string; amountPaid: number; changeGiven: number; referenceNumber?: string }) =>
    apiService.post<RestaurantOrderDTO>(`${API}/${id}/settle`, data),

  voidOrder: (id: number, reason: string) =>
    apiService.post<VoidOrderResponseDTO>(`${API}/${id}/void`, { reason }),

  voidKotItems: (id: number, data: VoidKotItemsDTO) =>
    apiService.post<VoidKotItemsResponseDTO>(`${API}/${id}/void-kot-items`, data),

  voidBill: (id: number, reason: string) =>
    apiService.post<RestaurantOrderDTO>(`${API}/${id}/void-bill`, { reason }),

  getBillHistory: (from: string, to: string, page = 1, pageSize = 50) =>
    apiService.get<{ data: BillHistoryItemDTO[] }>(`${API}/bill-history`, { from, to, page, pageSize }),

  // ── Receipt PDF — mirrors salesInvoiceService.viewInvoicePdf/downloadInvoicePdf ──

  /**
   * Opens the order receipt PDF in a new tab.
   *
   * IMPORTANT — call pattern to avoid popup blockers:
   *   const tab = window.open('', '_blank');          // open synchronously (user gesture)
   *   await restaurantOrderService.viewOrderPdf(id, num, tab);  // inject URL async
   *
   * If `targetTab` is omitted the function falls back to window.open, but that
   * will be blocked by most browsers because it runs after an async await chain.
   */
  viewOrderPdf: async (
    id: number | string,
    orderNumber: string,
    targetTab?: Window | null,
  ) => {
    const res = await apiClient.get<Blob>(`${API}/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);

    if (targetTab && !targetTab.closed) {
      targetTab.location.href = url;
    } else {
      window.open(url, '_blank');
    }

    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /** Download order receipt PDF with COPY watermark. */
  downloadOrderPdf: async (id: number | string, orderNumber: string) => {
    const res = await apiClient.get<Blob>(`${API}/${id}/pdf?watermark=COPY`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a   = document.createElement('a');
    a.href     = url;
    a.download = `Order_${orderNumber}_COPY.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
